import * as React from 'react';
import { TextField } from '@material-ui/core';
import { IObservableObject, IObservableValue, observable, observe } from 'mobx';
import { observer } from 'mobx-react';
import { FieldError } from '../../api/restApi';
import { translation } from '../../i18n';
import CustomDatePicker from '../../components/CustomDatePicker';
import EmployeeMultiSelect from '../../components/selects/EmployeeMultiSelect';
import { EmployeeDto } from '../user/userTypes';
import EmployeeSingleSelect from '../../components/selects/EmployeeSingleSelect';
import { QuestionCatalogueDto } from '../questionCatalogue/qustionCatalogueTypes';
import QuestionCatalogueSingleSelect from '../../components/selects/QuestionCatalogueSingleSelect';
import { QuestionnaireDto, QuestionnaireRequest } from './questionnaireTypes';
import { createPicker } from '../../util/picker';
import { getErrorMessages, renderRemainingErrors, textFieldErrorProps } from '../../util/formUtils';
import { Switch } from '@material-ui/core';
import { FormControlLabel } from '@material-ui/core';

interface Props {
  questionnaire: QuestionnaireRequest & IObservableObject;
  /** The existing quesitonnaire to be edited. Is undefined when creating a new one. */
  existingQuestionnaire?: QuestionnaireDto;
  dirtyState: IObservableValue<boolean>;
  fieldErrors: FieldError[];
}

export interface SelectedAppraisers {
  appraisers: EmployeeDto[]
}

export interface SelectedAppraisee {
  appraisee?: EmployeeDto
}

export interface SelectedQuestionCatalogue {
  questionCatalogue?: QuestionCatalogueDto
}

@observer
export class QuestionnaireForm extends React.Component<Props> {

  @observable private appraisers: SelectedAppraisers & IObservableObject = observable({appraisers: []});
  @observable private appraisee: SelectedAppraisee & IObservableObject = observable({appraisee: undefined});
  @observable private catalogue: SelectedQuestionCatalogue & IObservableObject = observable({questionCatalogue: undefined});

  constructor(props: Props) {
    super(props);
    this.init();
  }

  public componentDidMount() {
    observe(
      this.appraisers,
      () => this.props.questionnaire.appraisers = this.appraisers.appraisers
    );
    observe(
      this.appraisee,
      () => {
        if (this.appraisee.appraisee) {
          this.props.questionnaire.appraisee = this.appraisee.appraisee
        }
      }
    );
    observe(
      this.catalogue,
      () => {
        if (this.catalogue.questionCatalogue) {
          this.props.questionnaire.catalogue = this.catalogue.questionCatalogue
        }
      }
    );
  }

  public render() {
    const errorPicker = createPicker(this.props.fieldErrors);
    return (
      <>
        <EmployeeSingleSelect selectedEmployee={this.appraisee}/>
        <EmployeeMultiSelect selectedEmployees={this.appraisers}/>
        <QuestionCatalogueSingleSelect selectedCatalogue={this.catalogue} />
        <TextField
          id="questionnaireForm_input_title"
          label={translation.t('questionnaire.title')}
          margin="dense"
          fullWidth={true}
          required={true}
          value={this.props.questionnaire.title}
          onChange={ev => {
            this.props.questionnaire.title = ev.target.value
          }}
          {...textFieldErrorProps(errorPicker.pick(e => e.field === 'title'))}
        />
        <TextField
          id="questionnaireForm_input_description"
          label={translation.t('questionnaire.description')}
          margin="dense"
          fullWidth={true}
          value={this.props.questionnaire.description}
          onChange={ev => {
            this.props.questionnaire.description = ev.target.value
          }}
          {...textFieldErrorProps(errorPicker.pick(e => e.field === 'description'))}
        />
        <FormControlLabel
          id="questionnaireForm_switch_anonymous"
          control={
            <Switch
              checked={this.props.questionnaire.anonymous}
              onChange={ev => {
                this.props.questionnaire.anonymous = ev.target.checked
              }}
            />
          }
          label={translation.t('questionnaire.anonymous')}
        />
        <CustomDatePicker
          fullWidth={true}
          onChange={date => this.props.questionnaire.startDate = date}
          required={true}
          value={this.props.questionnaire.startDate}
          label={translation.t('questionnaire.startDate')}
          disablePast={false}
          invalidDateMessage={
            getErrorMessages(errorPicker.pick(e => e.field === 'startDate'))
          }
        />
        <CustomDatePicker
          fullWidth={true}
          onChange={date => this.props.questionnaire.endDate = date}
          required={true}
          value={this.props.questionnaire.endDate}
          label={translation.t('questionnaire.endDate')}
          disablePast={true}
          invalidDateMessage={
            getErrorMessages(errorPicker.pick(e => e.field === 'endDate'))
          }
        />
        {renderRemainingErrors(errorPicker.remaining())}
      </>
    )
  };

  private init = () => {
    if (this.props.existingQuestionnaire != null) {
      this.appraisers = observable({appraisers: this.props.existingQuestionnaire.appraisers});
      this.appraisee = observable({appraisee: this.props.existingQuestionnaire.appraisee});
      this.catalogue = observable({questionCatalogue: this.props.existingQuestionnaire.catalogue});
    }
  }

}
